import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import {
    BACKGROUND_PRESETS,
    resolvePreset,
    randomPreset,
    backgroundFontCss,
    backgroundTextClasses,
    backgroundTextStyleColor,
    backgroundTextStyleOutlineColor,
    backgroundDecorationKey,
    backgroundDecorationSymbols,
} from '../../lib/backgroundPostText';
import { BACKGROUND_PICKER_LIST } from '../../lib/fieldsHelpers';
import { articleBackgroundStyles } from '../../lib/articleBackground';

// Bottom drawer with one-tap styles for background posts. Every preset is
// rendered as a real mini card (same classes as the composer card), so what
// the author sees is the real CSS. Applying hands the resolved field values
// back to the composer through onApply and closes the drawer.
@observer
class BackgroundPresetsDrawer extends Component {
    applyPreset = (preset) => {
        const { onApply, drawerLevel = 1 } = this.props;
        const { appState } = this.props.stores;
        const fields = resolvePreset(preset, BACKGROUND_PICKER_LIST);
        if (fields && typeof onApply === 'function') {
            onApply(fields);
        }
        appState.toggleDrawer(false, drawerLevel);
    }

    choosePreset = (e) => {
        const preset = BACKGROUND_PRESETS.find(item => item.key === e.currentTarget.dataset.preset);
        if (preset) {
            this.applyPreset(preset);
        }
    }

    chooseRandom = () => {
        this.applyPreset(randomPreset());
    }

    renderPresetCard(preset) {
        const fields = resolvePreset(preset, BACKGROUND_PICKER_LIST);
        if (!fields) {
            return null;
        }
        const bg = articleBackgroundStyles({ background: fields.background });
        const fontCss = backgroundFontCss(fields.backgroundFont);
        const decoration = backgroundDecorationKey(fields.backgroundDecoration);
        const symbols = backgroundDecorationSymbols(decoration);
        const classes = ['background-composer-card background-preset-card', ...backgroundTextClasses(fields)].join(' ');
        return (
            <button
                type='button'
                key={`preset-${preset.key}`}
                class={classes}
                data-preset={preset.key}
                onClick={this.choosePreset}
                style={`
                    ${bg.backgroundImageCss ? `background-image: ${bg.backgroundImageCss};` : ''}
                    ${bg.backgroundColor ? `background-color: ${bg.backgroundColor};` : ''}
                    ${fontCss ? `font-family: ${fontCss};` : ''}
                    color: ${backgroundTextStyleColor(fields.backgroundTextStyle, bg.textColor)};
                    --background-text-outline: ${backgroundTextStyleOutlineColor(fields.backgroundTextStyle, bg.textOutlineColor)};
                `}
            >
                {symbols.length > 0 && <div class={`background-decoration-layer background-decoration-${decoration}`} aria-hidden='true'>
                    {symbols.map((symbol, idx) => <span class='background-decoration-item' key={`preset-decoration-${preset.key}-${idx}`}>{symbol}</span>)}
                </div>}
                <div class='background-composer-card-content'>
                    <div class='background-composer-card-text'>
                        <Text id={preset.textKey}>{preset.label}</Text>
                    </div>
                </div>
            </button>
        );
    }

    render() {
        return (
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100 px-3 pb-5' style='max-width: 960px;'>
                    <div class='font-weight-lighter px-0 box-header mt-3 d-flex justify-content-between align-items-center'>
                        <span><Text id='article.backgroundPresetsTitle'>Choose a style</Text></span>
                        <button type='button' class='btn btn-sm btn-outline-secondary' onClick={this.chooseRandom}>
                            <i class='fa-solid fa-shuffle me-1' />
                            <Text id='article.backgroundPresetRandom'>Random</Text>
                        </button>
                    </div>
                    <p class='small text-body-secondary px-0'>
                        <Text id='article.backgroundPresetsHelp'>A style sets background, font, text effect, animation and decoration in one go. You can still fine-tune everything afterwards.</Text>
                    </p>
                    <div class='background-preset-grid'>
                        {BACKGROUND_PRESETS.map(preset => this.renderPresetCard(preset))}
                    </div>
                </div>
            </div>
        );
    }
}

export default BackgroundPresetsDrawer;
